import React from 'react';
import {
  useWorkflowRenderCatalog,
  resolveTriggerRenderableNode,
} from '../../workflow-render-catalog';
import { WorkflowTriggerShell } from './workflow-trigger-shell';
import { WorkflowTriggerEnabledToggle } from './workflow-trigger-enabled-toggle';
import type { WorkflowTriggerHandles, WorkflowTriggerViewData } from './types';

export type WorkflowTriggerProps = {
  trigger: WorkflowTriggerViewData;
  handles?: WorkflowTriggerHandles;
  /**
   * Binding to hang the on/off switch off. Omitted on read-only surfaces (the
   * nested-workflow tree, the run-workflow preview), which draw the same card
   * but have nothing to edit.
   */
  bindingId?: string;
  /** Whether the trigger fires — see `isTriggerRunning` in `workflow-graph-to-flow`. */
  running?: boolean;
  className?: string;
};

/**
 * Renders a workflow trigger using the strategy for `trigger.kind`.
 */
export const WorkflowTrigger: React.FC<WorkflowTriggerProps> = ({
  trigger,
  handles,
  bindingId,
  running = true,
  className,
}) => {
  const catalog = useWorkflowRenderCatalog();
  const strategy = resolveTriggerRenderableNode(catalog, trigger).view;
  const title = strategy.getTitle(trigger);
  const description = strategy.getDescription?.(trigger);
  const icon = strategy.renderIcon?.(trigger);
  const body = strategy.renderBody(trigger);

  return (
    <WorkflowTriggerShell
      title={title}
      description={description}
      icon={icon}
      accent={strategy.accent}
      badge={strategy.badge}
      handles={handles}
      dimmed={running === false}
      className={className}
      enabledToggle={
        bindingId === undefined ? undefined : (
          <WorkflowTriggerEnabledToggle
            bindingId={bindingId}
            checked={running}
          />
        )
      }
    >
      {body}
    </WorkflowTriggerShell>
  );
};
